import ScrollReveal from "@/components/base/ScrollReveal";

export default function PrivacyPolicy() {
  return (
    <main>
      <section className="relative bg-bg-section pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 -right-32 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Legal
              </span>
              <h1 className="font-serif text-[36px] md:text-[52px] lg:text-[60px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Privacy Policy
              </h1>
              <p className="font-sans text-[16px] text-text-muted">
                Last updated: May 18, 2026
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="relative bg-bg py-16 md:py-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <div className="flex flex-col gap-12 font-sans text-[16px] text-text-body leading-[1.8]">

                {/* Plain-language summary box */}
                <div className="p-6 bg-bg-card border border-border rounded-xl">
                  <p className="font-semibold text-text-headline mb-2">The short version</p>
                  <p className="text-text-muted">
                    We don&apos;t track you, we don&apos;t sell your data, and we don&apos;t run any advertising or analytics on this site. The only personal information we ever receive is what you voluntarily send us — typically your name and email when you reach out or book a call. That&apos;s it.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    1. Who We Are
                  </h2>
                  <p className="text-text-muted">
                    VantageSight AI Advisory LLC (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) operates the website at vantagesight.readdy.co. We provide independent AI strategy, governance, workforce planning, and workshop and assessment design advisory services to organizations. This policy explains what limited personal information we handle and how we handle it.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    2. What Information We Collect
                  </h2>
                  <p className="text-text-muted mb-4">
                    We only collect information you actively give us. There are two situations where this happens:
                  </p>
                  <ul className="flex flex-col gap-3 list-disc pl-6">
                    <li className="text-text-muted">
                      <strong className="text-text-headline">Contact form:</strong> When you fill out the contact form on this site, we receive your name, email address, and the message you write. This is handled through Readdy (our website platform), not stored in any database we own or manage.
                    </li>
                    <li className="text-text-muted">
                      <strong className="text-text-headline">Calendly bookings:</strong> When you schedule a call, Calendly collects your name and email to confirm the appointment. That information is held by Calendly, not by us. See their privacy policy at{" "}
                      <a href="https://calendly.com/privacy" target="_blank" rel="noopener noreferrer nofollow" className="text-primary hover:underline">
                        calendly.com/privacy
                      </a>.
                    </li>
                  </ul>
                  <p className="text-text-muted mt-4">
                    We do not collect payment information, create user accounts, or maintain any customer database on this site.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    3. What We Do Not Collect
                  </h2>
                  <p className="text-text-muted mb-4">
                    To be explicit about what is <em>not</em> happening on this site:
                  </p>
                  <ul className="flex flex-col gap-2 list-disc pl-6">
                    <li className="text-text-muted">No analytics or tracking scripts (no Google Analytics, no Meta Pixel, no heatmaps)</li>
                    <li className="text-text-muted">No advertising cookies or retargeting</li>
                    <li className="text-text-muted">No collection of your IP address, browser type, or browsing behavior by us</li>
                    <li className="text-text-muted">No newsletter sign-up or email marketing list</li>
                    <li className="text-text-muted">No user accounts or stored profiles</li>
                  </ul>
                  <p className="text-text-muted mt-4">
                    Our website platform (Readdy) may collect basic server-side statistics such as page load counts for their own infrastructure purposes. We do not have access to or control over that data.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    4. How We Use Your Information
                  </h2>
                  <p className="text-text-muted">
                    When you contact us, we use the information you provide solely to respond to your inquiry or facilitate a consultation. We do not share your information with third parties, we do not add you to any mailing list, and we do not use it for any purpose beyond the conversation you initiated.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    5. Cookies
                  </h2>
                  <p className="text-text-muted">
                    This site uses one essential cookie: a small file that remembers you have acknowledged our cookie notice. This cookie does not track you, does not leave this site, and contains no personal information. No other cookies are set by us.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    6. Data Security
                  </h2>
                  <p className="text-text-muted">
                    Because we collect so little data — and none of it is stored in a database we control — the security risk is minimal. Contact form submissions are processed through Readdy&apos;s secure infrastructure. We do not store your messages beyond what is needed to respond to you.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    7. Your Rights &amp; Data Deletion
                  </h2>
                  <p className="text-text-muted">
                    If you have contacted us and would like us to delete your message or any information associated with you, simply email us and we will do so promptly — typically within a few business days.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    8. Children&apos;s Privacy
                  </h2>
                  <p className="text-text-muted">
                    This site is intended for business professionals and is not directed at individuals under 18. We do not knowingly collect information from minors. If you believe a minor has contacted us through this site, please let us know and we will delete the information immediately.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    9. Changes to This Policy
                  </h2>
                  <p className="text-text-muted">
                    If we make any meaningful changes to this policy, we will update the date at the top of this page. Given how little data we handle, significant changes are unlikely.
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-[24px] md:text-[28px] font-semibold text-text-headline mb-4">
                    10. Contact
                  </h2>
                  <p className="text-text-muted">
                    Questions about this policy? Reach out directly:
                  </p>
                  <div className="mt-4 p-6 bg-bg-card border border-border rounded-xl">
                    <p className="text-text-headline font-semibold mb-2">VantageSight AI Advisory LLC</p>
                    <p className="text-text-muted">
                      Email:{" "}
                      <a href="mailto:smurray@vantagesightai.com" className="text-primary hover:underline">
                        smurray@vantagesightai.com
                      </a>
                    </p>
                  </div>
                </div>

              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </main>
  );
}